/**
 * CAOS-1701 — the records this person opened, for the tab caret's "Recently viewed" section.
 *
 * The kernel keeps a per-user, per-object ring (CAOS-1478) which `data/get` fills on every record
 * read, and `search recent-list` reads back through the record-access plane. This module is the
 * host's side of that verb and nothing more: it asks, it validates what comes back, and it shapes
 * it into the `{ id, label, href }` the caret menu already takes for its list views.
 *
 * ── EVERY FAILURE IS AN EMPTY SECTION ───────────────────────────────────────────────────────────
 * The caret omits the section when it has no items rather than drawing an empty one, so there is no
 * such thing as a half-broken recents list: a fetch that fails, an answer that does not parse, or a
 * person who has opened nothing all produce the same, correct screen. That is deliberate and it is
 * why nothing here throws — a menu of saved views must open instantly whether or not this answers.
 * It is also why the kernel counts a dropped WRITE under its own observability code: the symptom is
 * identical at the surface, so the only place the difference can be seen is the server.
 */

/** A minimal response shape, so a test stub need not build a whole `Response`. */
export interface RecentItemsFetchResponse {
  json(): Promise<unknown>;
}

/** The fetch seam: a same-origin GET against the host `/api/*` proxy. */
export type RecentItemsFetch = (path: string) => Promise<RecentItemsFetchResponse>;

const defaultRecentItemsFetch: RecentItemsFetch = (path) =>
  fetch(path, {
    method: 'GET',
    headers: { accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  }) as unknown as Promise<RecentItemsFetchResponse>;

let recentItemsFetch: RecentItemsFetch = defaultRecentItemsFetch;

/** Override the recents fetch seam (tests); `null` restores the default. */
export function setRecentItemsFetch(fn: RecentItemsFetch | null): void {
  recentItemsFetch = fn ?? defaultRecentItemsFetch;
}

/** One entry in the caret's recently-viewed section. */
export interface CaretRecent {
  readonly id: string;
  readonly label: string;
  readonly href: string;
}

/** How many the caret shows. The verb caps its own answer; this is the menu's appetite. */
export const CARET_RECENTS_LIMIT = 5;

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
// Trimmed before the emptiness test, not after: a name of "   " is as unchoosable in a menu as ""
// is, and only the untrimmed check let it through as a blank row.
const str = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t === '' ? null : t;
};

/**
 * The recently-viewed records for one object, newest first.
 *
 * A record whose NAME the caller may not read comes back with `name: null`, and is dropped here: a
 * menu entry with no label is not something a person can choose between, and inventing one from the
 * id would be worse. That is the same decision the kernel makes by returning null rather than
 * guessing — it is not an error, it is a record this person can open but not name.
 */
export async function recentsFor(
  object: string,
  hrefFor: (recordId: string) => string,
  limit: number = CARET_RECENTS_LIMIT,
): Promise<CaretRecent[]> {
  try {
    const res = await recentItemsFetch(
      `/api/search/recent-list?object=${encodeURIComponent(object)}&limit=${encodeURIComponent(String(limit))}`,
    );
    const body: unknown = await res.json();
    if (!isRecord(body) || body.ok !== true || !isRecord(body.data)) return [];
    const items = body.data.items;
    if (!Array.isArray(items)) return [];
    const out: CaretRecent[] = [];
    for (const item of items) {
      if (!isRecord(item)) continue;
      const id = str(item.recordId);
      const label = str(item.name);
      if (!id || !label) continue;
      out.push({ id, label, href: hrefFor(id) });
      if (out.length >= limit) break;
    }
    return out;
  } catch {
    return [];
  }
}
